import React, { ReactNode, PropsWithChildren } from "react";
import { useRouter } from "next/router";
import {
  Flex,
  Box,
  Text,
  Divider,
  Button,
  useColorModeValue,
} from "@chakra-ui/react";

import Slider from "react-slick";
import _values from "lodash/values";

import DefiCard from "./DefiCard";

import { useCrossChainData } from "ui/CrossChain/crossChainHook";
import { AnimatedNumber } from "ui/AnimatedNumber/AnimatedNumber";

// hooks
import useHashConnect from "hooks/useHashConnect";

// Constants
import { slickSettings } from "./constants";

// Utils
import { getNumbersFromValue } from "utils";

// Images
import hbarxDarkIcon from "assets/hbarxDark.svg";
import hbarxLightIcon from "assets/hbarxLight.svg";
import hbarDarkIcon from "assets/hederaDark.svg";
import hbarLightIcon from "assets/hederaLight.svg";

import { getDefiData, getDefiOptions } from "constants/defi";
import { DefiDataValues } from "types/DefiData";

// analytics
import { trackEvent } from "utils/firebase";
import { EXPLORE_DEFI_CLICK } from "constants/analytics";
const path = process.env.NEXT_PUBLIC_SUB_ROUTE || "";

const SliderFixed = Slider as unknown as React.FC<
  PropsWithChildren<{ children: ReactNode }>
>;

const DefiPreview = () => {
  const hbarxIcon = useColorModeValue(hbarxLightIcon, hbarxDarkIcon);
  const hbarIcon = useColorModeValue(hbarLightIcon, hbarDarkIcon);

  const router = useRouter();
  const { CCTVL } = useCrossChainData();
  const defiOptions = getDefiOptions({
    hbarIcon: hbarIcon,
    hbarxIcon: hbarxIcon,
  });

  const { defiData: data } = useHashConnect();
  const defiData: DefiDataValues = getDefiData(data);

  const getMaxAPY = () => {
    const apys = _values(defiData).map(({ apy }) =>
      getNumbersFromValue(apy.toString())
    );

    return Math.ceil(Math.max(...apys));
  };

  return (
    <Box
      bg="brand.backgroundLight"
      boxShadow="0px 4px 12px rgba(53, 56, 140, 0.05)"
      borderRadius="8px"
      p="1.5rem"
    >
      <Flex align="center" justifyContent="center">
        <Text
          as="span"
          fontSize="16px"
          color="brand.textInactive"
          textAlign="center"
          mr="1.5rem"
        >
          Stader TVL
        </Text>
        <Text as="span" fontSize="1.25rem" lineHeight="1.5rem" height="1.5rem">
          {CCTVL && "$ "}
          {CCTVL ? (
            <AnimatedNumber number={CCTVL} separator="," decimals={0} />
          ) : (
            "N/A"
          )}
        </Text>
      </Flex>
      <Divider borderColor="brand.separator" my="1.5rem" />
      <Flex
        align="center"
        justifyContent="space-between"
        mb="2rem"
        fontSize={{ base: "14px", lg: "1rem" }}
        gap="0.5rem"
      >
        <Box>
          <Text>Get upto {getMaxAPY()}% yields</Text>
          <Text color="brand.textInactive">Explore Defi opportunities</Text>
        </Box>
        <Button
          minW="120px"
          borderRadius="0.5rem"
          cursor="pointer"
          p={{ base: "0.5rem 1rem", lg: "0.5rem 2rem" }}
          fontSize="14px"
          onClick={() => {
            router.push({ pathname: path + "/lt/defi", query: router.query });
            trackEvent(EXPLORE_DEFI_CLICK);
          }}
          background="brand.primary"
          color="brand.textWhite"
        >
          Explore DeFi
        </Button>
      </Flex>
      <Box mb="1.5rem">
        <SliderFixed {...slickSettings}>
          {defiOptions.map((option) => (
            <DefiCard
              key={option.partnerName}
              icon={option.icon}
              token1={option.token1}
              token1Icon={option.token1Icon}
              token1IconWidth={option?.token1IconWidth}
              token1IconHeight={option?.token1IconHeight}
              token2={option.token2}
              token2Icon={option.token2Icon}
              partnerName={option.partnerName}
              link={option.link}
              apy={defiData[option.id].apy}
              tvl={defiData[option.id].tvl}
              showTVL={option.showTvl}
            />
          ))}
        </SliderFixed>
      </Box>
    </Box>
  );
};

export default DefiPreview;
